import { useEffect, useRef } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Avatar, keyframes } from '@mui/material';
// components
import Scrollbar from '@/components/scrollbar';
import ChatMessageItem from '../chat/message/ChatMessageItem';
import Iconify from '@/components/iconify';
// types
import { ConversationItemForUserChat, MessageToSendItemForChat } from '../../redux/coreApi';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(3),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 320,
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const dotAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
`;

const Dot = styled('div')<{ delay: number }>(({ delay }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: 'currentColor',
  animation: `${dotAnimation} 1.4s infinite`,
  animationDelay: `${delay}s`,
}));

// ----------------------------------------------------------------------

type CoachMessageListProps = {
  messages: MessageToSendItemForChat[];
  conversation: ConversationItemForUserChat;
  isCoachTyping?: boolean;
};

export default function CoachMessageList({
  messages,
  conversation,
  isCoachTyping = false,
}: Readonly<CoachMessageListProps>) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isCoachTyping]);

  const handleOpenLightbox = (imageUrl: string) => {
    // Lightbox functionality can be added if needed
    console.log('Open lightbox for:', imageUrl);
  };

  return (
    <Scrollbar scrollableNodeProps={{ ref: scrollRef }} sx={{ p: 3, height: 1 }}>
      {messages
        .filter(
          ({ message = '', attachments = [], contentTypes = [] }) =>
            !!message.length || !!attachments.length || contentTypes.includes('video')
        )
        .map((message) => (
          <ChatMessageItem
            key={message.id}
            message={message}
            conversation={conversation}
            onOpenLightbox={handleOpenLightbox}
          />
        ))}

      {isCoachTyping && (
        <RootStyle>
          <Box sx={{ display: 'flex' }}>
            <Avatar alt={conversation.brandName} sx={{ width: 32, height: 32, mr: 2 }}>
              <Iconify icon="ic:round-assistant" width={20} height={20} />
            </Avatar>
            <div>
              <ContentStyle>
                <Dot delay={0} />
                <Dot delay={0.2} />
                <Dot delay={0.4} />
              </ContentStyle>
            </div>
          </Box>
        </RootStyle>
      )}
    </Scrollbar>
  );
}
